import { registerConfig, type AppConfig } from "@um/ui/appConfig";
import { getAttributionData as getAttributionDataWeb } from "@um/ui/utm";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import {
  FirebaseRemoteConfig,
  GetValueSource,
} from "@capacitor-firebase/remote-config";
import { getAttributionData, setUserId } from "./services/socialAttribution";

type SentryNativeInit = NonNullable<
  NonNullable<AppConfig["sentry"]>["nativeInit"]
>;
import { FeedbackVariantId } from "./services/feedback";

const config: AppConfig = registerConfig({
  name: "App",
  offlinePlayback: typeof window !== "undefined" && window.location.pathname.startsWith("/offline/"),
  isNativePlatform: Capacitor.isNativePlatform(),
  getPlatform: Capacitor.getPlatform,
  production: {
    apiKey: "AIzaSyClC86vpf5B4SabAmL-WjkcGlHBuA_LmYc",
    authDomain: "uniqueminds-prod.firebaseapp.com",
    projectId: "uniqueminds-prod",
    storageBucket: "uniqueminds-prod.firebasestorage.app",
    messagingSenderId: "19738907440",
    appId: "1:19738907440:web:34a17598603e389f7aeef5",
    measurementId: "G-Y3950DJRSH",
  },
  staging: {
    apiKey: "AIzaSyBeQ_ho8Zv0oYKsecdWSqQ3s1XvQRTVzpA",
    authDomain: "uniqueminds-staging.firebaseapp.com",
    projectId: "uniqueminds-staging",
    storageBucket: "uniqueminds-staging.firebasestorage.app",
    messagingSenderId: "389600537721",
    appId: "1:389600537721:web:c13a2614971de48eedb8a8",
    measurementId: "G-2HYQ5L10PG",
  },
  productionDomains: [
    "uniqueminds-prod.web.app",
    "app.uniqueminds.com",
    "uniqueminds.app",
  ],
  nativeAuthentication: {
    signInWithGoogle: async () => {
      // useCredentialManager: false uses the traditional Google Sign-In flow
      // which prompts users to sign in even without existing credentials on the device.
      // The default Credential Manager (true) only works if a Google account is already
      // set up on the device, which fails on emulators and fresh installs.
      const result = await FirebaseAuthentication.signInWithGoogle({
        useCredentialManager: false,
      });
      return result.credential;
    },
    signInWithApple: async () => {
      const result = await FirebaseAuthentication.signInWithApple();
      return result.credential;
    },
    signOut: async () => {
      // Sign out from the native Google/Apple session
      // This clears the cached credentials so the account picker shows again
      await FirebaseAuthentication.signOut();
    },
  },
  nativeAnalytics: {
    logEvent: (event: string, params?: Record<string, any>) =>
      FirebaseAnalytics.logEvent({ name: event, params }),
    setUserId: (userId: string) => {
      FirebaseAnalytics.setUserId({ userId });
      setUserId(userId);
    },
    setUserProperty: (key: string, value: string) =>
      FirebaseAnalytics.setUserProperty({ key, value }),
    getAppInstanceId: async () => {
      const { appInstanceId } = await FirebaseAnalytics.getAppInstanceId();
      return appInstanceId;
    },
  },
  // Native Remote Config: fetches present the ANDROID/iOS app identity (the
  // web SDK presents the web one), which is what lets Firebase A/B join
  // variant exposure with native Analytics goal events.
  // Gated on plugin availability: this JS bundle also reaches OLD binaries via
  // OTA, and those don't have the plugin compiled in — they must keep the web
  // Remote Config path or every RC read would fall back to JS defaults.
  nativeRemoteConfig: Capacitor.isPluginAvailable("FirebaseRemoteConfig")
    ? {
        fetchAndActivate: () => FirebaseRemoteConfig.fetchAndActivate(),
        getValue: async (key: string) => {
          const { value, source } = await FirebaseRemoteConfig.getString({
            key,
          });
          return {
            value,
            source:
              source === GetValueSource.Remote
                ? ("remote" as const)
                : source === GetValueSource.Default
                  ? ("default" as const)
                  : ("static" as const),
          };
        },
        setSettings: (settings: {
          fetchTimeoutInSeconds?: number;
          minimumFetchIntervalInSeconds?: number;
        }) => FirebaseRemoteConfig.setSettings(settings),
      }
    : undefined,
  sentry: import.meta.env.VITE_USE_EMULATORS
    ? undefined
    : {
        projectId:
          "https://febd00f56660343faa903ec0c4bf1ed1@ums.uniqueminds.app/4509719746445312",
        recordReplays: true,
        // Native only. @sentry/capacitor wraps the native crash reporters, and
        // on the web it just forwards to the browser SDK — which telemetry.ts
        // calls directly when this is undefined. Leaving it defined on web
        // meant every web visitor downloaded the Capacitor SDK (and, because
        // that package is not marked side-effect-free, Session Replay with
        // it). The import is dynamic so it stays out of the web entry chunk.
        nativeInit: Capacitor.isNativePlatform()
          ? (((options, forwardInit) => {
              void import("@sentry/capacitor").then(({ init }) =>
                init(options as never, forwardInit as never)
              );
            }) as SentryNativeInit)
          : undefined,
      },
  updateActivityViewed: true,
  getAttributionData: Capacitor.isNativePlatform()
    ? getAttributionData
    : getAttributionDataWeb,
  remoteConfig: {
    FeedbackStrategy: undefined as FeedbackVariantId | undefined,
    /** Onboarding routine-menu A/B: "control" (original 3 options) | "juicy" (outcome-based menu). */
    RoutineMenuVariant: "control" as string,
  } as const,
});

export default config;
